import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Plus, Star, Quote, Truck, RefreshCw, Shield, Clock, Instagram, Sparkles, ChevronRight, ChevronLeft, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 6: SYMMETRY / HERITAGE
 * Mobile: Traditional Indian, Ethnic Wear (HERITAGE)
 * Desktop: Editorial, Magazine-inspired, Clean Grid (SYMMETRY)
 */
const Template6 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [activeEditorial, setActiveEditorial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % mobileTestimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) =>
                diff > 0 ? (prev + 1) % mobileTestimonials.length : (prev - 1 + mobileTestimonials.length) % mobileTestimonials.length
            );
        }
    };

    const toggleWishlist = (id, e) => {
        e.stopPropagation();
        setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // Mobile data (HERITAGE)
    const mobileCategories = [
        { name: 'Banarasi Sarees', count: '250+ Styles', image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=600&h=800&fit=crop' },
        { name: 'Bridal Lehengas', count: '180+ Designs', image: 'https://images.unsplash.com/photo-1604335399105-a0c585fd81a1?w=600&h=800&fit=crop' },
        { name: 'Kurta Sets', count: '320+ Pieces', image: 'https://images.unsplash.com/photo-1594463750939-ebb28c3f7f75?w=600&h=800&fit=crop' },
        { name: 'Festive Wear', count: '150+ Collections', image: 'https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=600&h=800&fit=crop' },
    ];

    const mobileProducts = [
        { id: 1, name: 'Chanderi Silk Saree', price: 8499, originalPrice: 11999, badge: 'BESTSELLER', image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&h=650&fit=crop' },
        { id: 2, name: 'Zardozi Embroidered Lehenga', price: 15999, originalPrice: null, badge: 'NEW', image: 'https://images.unsplash.com/photo-1604335399105-a0c585fd81a1?w=500&h=650&fit=crop' },
        { id: 3, name: 'Block Print Anarkali', price: 4299, originalPrice: 5999, badge: 'SALE', image: 'https://images.unsplash.com/photo-1594463750939-ebb28c3f7f75?w=500&h=650&fit=crop' },
        { id: 4, name: 'Banarasi Brocade Set', price: 6799, originalPrice: null, badge: 'TRENDING', image: 'https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=500&h=650&fit=crop' },
        { id: 5, name: 'Chikankari Palazzo Suit', price: 3599, originalPrice: 4999, badge: 'SALE', image: 'https://images.unsplash.com/photo-1583391733981-8b530e07ece4?w=500&h=650&fit=crop' },
        { id: 6, name: 'Temple Jewelry Set', price: 2499, originalPrice: null, badge: 'NEW', image: 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=500&h=650&fit=crop' },
    ];

    const mobileTestimonials = [
        { text: 'The Banarasi saree I bought for my daughter\'s wedding was absolutely stunning. The zari work and color are exactly as shown.', name: 'Priya Sharma', location: 'Mumbai', rating: 5 },
        { text: 'I have been buying ethnic wear for years, but the quality and authenticity of Heritage\'s handloom collection is unmatched.', name: 'Ananya Patel', location: 'Ahmedabad', rating: 5 },
        { text: 'Perfect festive wear! The Anarkali I ordered fit beautifully and the embroidery detail is exquisite. Highly recommended.', name: 'Meera Krishnan', location: 'Chennai', rating: 5 },
    ];

    const mobileInstagramImages = [
        'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1604335399105-a0c585fd81a1?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1594463750939-ebb28c3f7f75?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1583391733981-8b530e07ece4?w=400&h=400&fit=crop',
    ];

    // Desktop data (SYMMETRY)
    const desktopCategories = [
        { name: 'Kurtas & Kurtis', count: '180+ Styles', image: 'https://placehold.co/600x800/FEF7ED/1C1917?text=Kurtas' },
        { name: 'Sarees', count: '250+ Styles', image: 'https://placehold.co/600x800/FEF3C7/1C1917?text=Sarees' },
        { name: 'Lehengas', count: '120+ Styles', image: 'https://placehold.co/600x800/FDE68A/1C1917?text=Lehengas' },
        { name: 'Indo-Western', count: '95+ Styles', image: 'https://placehold.co/600x800/FEF7ED/1C1917?text=Indo-Western' }
    ];

    const desktopProducts = [
        { id: 1, name: 'Chanderi Silk Kurta Set', price: '\u20b94,299', originalPrice: '\u20b95,999', badge: 'Bestseller', image: 'https://placehold.co/400x500/FFFBEB/1C1917?text=Kurta+1' },
        { id: 2, name: 'Banarasi Zari Saree', price: '\u20b98,499', originalPrice: '\u20b911,999', badge: 'Handwoven', image: 'https://placehold.co/400x500/FEF7ED/1C1917?text=Saree+1' },
        { id: 3, name: 'Embroidered Anarkali', price: '\u20b96,799', originalPrice: '\u20b98,999', badge: 'New', image: 'https://placehold.co/400x500/FEF3C7/1C1917?text=Anarkali' },
        { id: 4, name: 'Palazzo Kurta Combo', price: '\u20b93,599', originalPrice: '\u20b94,999', badge: 'Trending', image: 'https://placehold.co/400x500/FFFBEB/1C1917?text=Palazzo' },
        { id: 5, name: 'Designer Lehenga Set', price: '\u20b915,999', originalPrice: '\u20b922,999', badge: 'Premium', image: 'https://placehold.co/400x500/FEF7ED/1C1917?text=Lehenga' },
        { id: 6, name: 'Cotton Printed Kurti', price: '\u20b91,299', originalPrice: '\u20b91,799', badge: 'Daily Wear', image: 'https://placehold.co/400x500/FEF3C7/1C1917?text=Kurti' }
    ];

    const editorials = [
        {
            title: 'The Art of Handloom',
            subtitle: 'Celebrating Indian Weaving Heritage',
            description: 'From Varanasi to Kanchipuram, discover the timeless artistry of Indian handlooms. Each piece tells a story of generations of skilled artisans.',
            image: 'https://placehold.co/1200x700/FEF7ED/1C1917?text=Handloom+Editorial',
            cta: 'Explore Collection'
        },
        {
            title: 'Modern Maharani',
            subtitle: 'Contemporary Bridal Couture',
            description: 'Redefining bridal elegance with modern silhouettes and traditional craftsmanship. For the bride who celebrates both heritage and individuality.',
            image: 'https://placehold.co/1200x700/FEF3C7/1C1917?text=Bridal+Editorial',
            cta: 'View Bridal'
        },
        {
            title: 'Festive Splendor',
            subtitle: 'Diwali Collection 2024',
            description: 'Illuminate your celebrations with our curated festive edit. Rich fabrics, intricate embroideries, and colors that capture the spirit of festivities.',
            image: 'https://placehold.co/1200x700/FDE68A/1C1917?text=Festive+Editorial',
            cta: 'Shop Festive'
        }
    ];

    const desktopTestimonials = [
        { text: 'The quality of the Banarasi saree exceeded my expectations. The zari work is exquisite and perfect for my daughter\'s wedding.', name: 'Priya Sharma', location: 'Mumbai', rating: 5 },
        { text: 'Finally found a brand that understands modern Indian aesthetics. Their kurta sets are perfect for both office and festivals.', name: 'Ananya Patel', location: 'Bangalore', rating: 5 },
        { text: 'The attention to detail in their embroidery work is remarkable. I\'ve received so many compliments on my lehenga!', name: 'Meera Reddy', location: 'Hyderabad', rating: 5 }
    ];

    const desktopInstagramPosts = [
        { image: 'https://placehold.co/300x300/FEF7ED/1C1917?text=Insta+1', likes: '2.4k' },
        { image: 'https://placehold.co/300x300/FEF3C7/1C1917?text=Insta+2', likes: '1.8k' },
        { image: 'https://placehold.co/300x300/FFFBEB/1C1917?text=Insta+3', likes: '3.1k' },
        { image: 'https://placehold.co/300x300/FDE68A/1C1917?text=Insta+4', likes: '2.7k' },
        { image: 'https://placehold.co/300x300/FEF7ED/1C1917?text=Insta+5', likes: '1.9k' },
        { image: 'https://placehold.co/300x300/FEF3C7/1C1917?text=Insta+6', likes: '2.2k' }
    ];

    return (
        <div className="min-h-screen overflow-x-hidden font-sans">

            {/* ========== MOBILE WRAPPER (HERITAGE) ========== */}
            <div className="lg:hidden bg-amber-50/30 text-stone-900">

                {/* ANNOUNCEMENT BAR - Mobile */}
                <div className="bg-gradient-to-r from-amber-800 via-amber-700 to-amber-800 text-amber-50 py-2.5 text-center">
                    <p className="text-[11px] sm:text-xs tracking-[0.15em] uppercase">Free Shipping Pan-India on Orders Above Rs.999 &bull; Authentic Handloom Guaranteed</p>
                </div>

                {/* HERO - Mobile Stacked */}
                <section>
                    <div className="relative aspect-[4/5]">
                        <img src="https://images.unsplash.com/photo-1610173826608-e3a1f460e202?w=1920&q=80" alt="Heritage Collection" className="w-full h-full object-cover" />
                    </div>
                    <div className="bg-gradient-to-b from-amber-50 to-white px-5 py-8 text-center">
                        <span className="text-[11px] tracking-[0.25em] uppercase text-amber-700 font-semibold">Celebrating Indian Craft</span>
                        <h1 className="text-2xl sm:text-3xl font-light text-stone-900 mt-2 mb-3 leading-tight">
                            Weaving <span className="italic font-serif text-amber-700">Traditions</span> Into Tomorrow
                        </h1>
                        <p className="text-[14px] text-stone-500 mb-6 max-w-sm mx-auto leading-relaxed">
                            Handcrafted ethnic wear that honours the legacy of Indian artisans, reimagined for the modern woman.
                        </p>
                        <button
                            onClick={() => navigate('/template6/shop')}
                            className="w-full bg-stone-900 text-white py-4 text-sm tracking-[0.15em] uppercase font-medium min-h-[48px] rounded-none"
                        >
                            Explore Collection
                        </button>
                    </div>
                </section>

                {/* CATEGORIES - Mobile horizontal scroll */}
                <section className="py-10 sm:py-16 bg-white">
                    <div className="px-5 sm:px-6 mb-6">
                        <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-amber-700 font-semibold">Shop By Category</span>
                        <h2 className="text-xl sm:text-2xl font-light text-stone-900 mt-1">Curated Collections</h2>
                    </div>
                    <div className="relative">
                        <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                            {mobileCategories.map((cat, i) => (
                                <div key={i} onClick={() => navigate('/template6/shop')} className="flex-none w-[68vw] snap-start cursor-pointer group">
                                    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden">
                                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent" />
                                        <div className="absolute bottom-4 left-4 right-4">
                                            <h3 className="text-white text-lg font-medium">{cat.name}</h3>
                                            <p className="text-white/70 text-xs mt-0.5">{cat.count}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="absolute right-0 top-0 bottom-4 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                    </div>
                </section>

                {/* PRODUCTS - Mobile horizontal scroll */}
                <section className="py-10 sm:py-16 bg-amber-50/50">
                    <div className="px-5 sm:px-6 flex items-end justify-between mb-6">
                        <div>
                            <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-amber-700 font-semibold">New Arrivals</span>
                            <h2 className="text-xl sm:text-2xl font-light text-stone-900 mt-1">Editor's Picks</h2>
                        </div>
                    </div>
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                        {mobileProducts.map((product) => (
                            <div key={product.id} onClick={() => navigate('/template6/shop')} className="flex-none w-[44vw] snap-start cursor-pointer group">
                                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-2 bg-amber-50">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                    {product.badge && (
                                        <span className={`absolute top-2 left-2 px-2 py-0.5 text-[10px] font-semibold tracking-wide rounded-full ${product.badge === 'SALE' ? 'bg-red-500 text-white' : product.badge === 'NEW' ? 'bg-amber-700 text-white' : product.badge === 'BESTSELLER' ? 'bg-amber-500 text-stone-900' : 'bg-stone-800 text-white'}`}>
                                            {product.badge}
                                        </span>
                                    )}
                                    <button onClick={(e) => toggleWishlist(product.id, e)} className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                                        <Heart className={`w-3.5 h-3.5 ${wishlist[product.id] ? 'fill-red-500 text-red-500' : 'text-stone-600'}`} />
                                    </button>
                                    <button className="absolute bottom-2 right-2 w-8 h-8 bg-stone-900/90 rounded-full flex items-center justify-center">
                                        <Plus className="w-3.5 h-3.5 text-white" />
                                    </button>
                                </div>
                                <h3 className="text-[13px] font-medium text-stone-900 line-clamp-1">{product.name}</h3>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[13px] font-semibold text-stone-900">Rs.{product.price.toLocaleString()}</span>
                                    {product.originalPrice && <span className="text-[11px] text-stone-400 line-through">Rs.{product.originalPrice.toLocaleString()}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="px-5 mt-4">
                        <button onClick={() => navigate('/template6/shop')} className="w-full py-3.5 border border-stone-900 text-stone-900 text-sm tracking-[0.1em] uppercase font-medium min-h-[48px]">
                            View All Products
                        </button>
                    </div>
                </section>

                {/* BRAND STORY - Mobile */}
                <section className="py-10 sm:py-16 bg-stone-900 text-white">
                    <div>
                        <div className="sm:rounded-2xl sm:mx-6 overflow-hidden">
                            <img src="https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=800&h=1000&fit=crop" alt="Indian Craftsmanship" className="w-full aspect-[4/5] object-cover" />
                        </div>
                        <div className="px-5 sm:px-6 py-8">
                            <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-amber-400 font-semibold">Our Heritage</span>
                            <h2 className="text-xl sm:text-2xl font-light text-white mt-2 mb-4 leading-snug">
                                Celebrating Indian <span className="italic font-serif text-amber-300">Craftsmanship</span>
                            </h2>
                            <p className="text-[14px] sm:text-base text-stone-300 leading-relaxed mb-4">
                                Every thread tells a story. From the silk weavers of Varanasi to the block printers of Jaipur,
                                we work directly with artisan communities across India, ensuring fair wages and preserving
                                traditional techniques for future generations.
                            </p>
                            <p className="text-[14px] sm:text-base text-stone-400 leading-relaxed mb-6">
                                Each piece is a testament to centuries of skill passed down through generations, now meeting
                                contemporary design sensibilities for the modern Indian woman.
                            </p>
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                {[
                                    { icon: Shield, title: 'Authentic Handloom', desc: 'GI tagged products' },
                                    { icon: Truck, title: 'Free Shipping', desc: 'Pan-India delivery' },
                                    { icon: RefreshCw, title: 'Easy Returns', desc: '15-day return policy' },
                                    { icon: Clock, title: 'COD Available', desc: 'Cash on delivery' },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-start gap-2.5">
                                        <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                                            <item.icon className="w-4 h-4 text-amber-400" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-white">{item.title}</p>
                                            <p className="text-xs text-stone-400">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button onClick={() => navigate('/template6/shop')} className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-3.5 bg-amber-500 text-stone-900 text-sm font-semibold tracking-wider uppercase hover:bg-amber-400 transition-colors min-h-[48px]">
                                Meet Our Artisans <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </section>

                {/* TESTIMONIALS - Mobile swipeable */}
                <section className="py-10 sm:py-16 bg-amber-50/50">
                    <div className="px-5 sm:px-6">
                        <div className="text-center mb-6">
                            <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-amber-700 font-semibold">Customer Stories</span>
                            <h2 className="text-xl sm:text-2xl font-light text-stone-900 mt-1">What They're Saying</h2>
                        </div>
                        <div className="overflow-hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                            <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                                {mobileTestimonials.map((t, i) => (
                                    <div key={i} className="w-full flex-none px-1">
                                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                                            <Quote className="w-8 h-8 text-amber-200 mb-3" />
                                            <div className="flex gap-0.5 mb-3">{[...Array(t.rating)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                                            <p className="text-[14px] text-stone-600 leading-relaxed mb-4">"{t.text}"</p>
                                            <div className="border-t border-stone-100 pt-3">
                                                <p className="text-sm font-medium text-stone-900">{t.name}</p>
                                                <p className="text-xs text-stone-500">{t.location}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-center gap-2 mt-4">
                            {mobileTestimonials.map((_, i) => (
                                <button key={i} onClick={() => setActiveTestimonial(i)} className={`h-1.5 rounded-full transition-all duration-300 ${activeTestimonial === i ? 'w-6 bg-amber-700' : 'w-1.5 bg-stone-300'}`} />
                            ))}
                        </div>
                    </div>
                </section>

                {/* FEATURES - Mobile horizontal scroll */}
                <section className="py-10 sm:py-16 bg-white">
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-2">
                        {[
                            { icon: Truck, title: 'Free Shipping', desc: 'On orders above Rs.999' },
                            { icon: RefreshCw, title: 'Easy Returns', desc: '15-day return policy' },
                            { icon: Shield, title: 'Secure Payment', desc: '100% secure checkout' },
                            { icon: Clock, title: 'COD Available', desc: 'Pay on delivery' },
                        ].map((feature, i) => (
                            <div key={i} className="flex-none w-[55vw] snap-start flex items-start gap-3 bg-amber-50/70 rounded-2xl p-4">
                                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                                    <feature.icon className="w-4 h-4 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-stone-900">{feature.title}</p>
                                    <p className="text-xs text-stone-500 mt-0.5">{feature.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* NEWSLETTER - Mobile */}
                <section className="py-10 sm:py-16 bg-gradient-to-br from-amber-100 via-orange-50 to-amber-50">
                    <div className="px-5 sm:px-6 text-center">
                        <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-amber-700 font-semibold">Stay Connected</span>
                        <h2 className="text-xl sm:text-2xl font-light text-stone-900 mt-2 mb-3">Join Our Community</h2>
                        <p className="text-[14px] sm:text-base text-stone-600 mb-6">
                            Subscribe for exclusive previews, styling tips, and special offers. Plus, get 15% off your first order!
                        </p>
                        <form className="flex flex-col gap-3 max-w-md mx-auto">
                            <input type="email" placeholder="Enter your email" className="flex-1 px-5 py-3.5 bg-white border border-stone-200 rounded-xl text-stone-900 placeholder-stone-400 focus:outline-none focus:border-amber-500 transition-colors" />
                            <button type="submit" className="px-8 py-3.5 bg-stone-900 text-white text-sm tracking-[0.1em] uppercase font-semibold rounded-xl hover:bg-stone-800 transition-colors min-h-[48px]">Subscribe</button>
                        </form>
                        <p className="text-stone-500 text-[11px] sm:text-xs mt-4">No spam, unsubscribe anytime. Privacy policy applies.</p>
                    </div>
                </section>

                {/* INSTAGRAM - Mobile 3-col */}
                <section className="py-10 sm:py-16 bg-white">
                    <div className="text-center mb-6 px-5">
                        <div className="flex items-center justify-center gap-2 mb-1">
                            <Instagram className="w-5 h-5 text-stone-900" />
                            <span className="text-sm text-stone-900 font-medium">@heritagefashion</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-light text-stone-900">Follow Our Journey</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-0.5">
                        {mobileInstagramImages.map((img, i) => (
                            <div key={i} className="relative aspect-square overflow-hidden group cursor-pointer">
                                <img src={img} alt={`Post ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/30 transition-colors flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

            </div>

            {/* ========== DESKTOP WRAPPER (ORIGINAL SYMMETRY THEME) ========== */}
            <div className="hidden lg:block bg-amber-50/30 font-sans">

                {/* Hero - Magazine Cover Style */}
                <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50/30 to-amber-100/50">
                    <div className="absolute top-20 left-10 w-32 h-32 bg-amber-200/30 rounded-full blur-3xl" />
                    <div className="absolute bottom-40 right-20 w-48 h-48 bg-orange-200/30 rounded-full blur-3xl" />
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-screen py-16">
                            <div className="text-left">
                                <span className="inline-block text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase mb-4">Editorial Collection</span>
                                <h1 className="text-6xl xl:text-7xl font-light text-stone-900 leading-[1.1] mb-6">
                                    Weaving
                                    <span className="block font-serif italic text-amber-700">Traditions</span>
                                    Into Tomorrow
                                </h1>
                                <p className="text-stone-600 text-lg lg:text-xl max-w-lg mb-8 leading-relaxed">
                                    Discover the essence of Indian craftsmanship reimagined for the modern woman.
                                    Where heritage meets contemporary elegance.
                                </p>
                                <div className="flex gap-4">
                                    <button onClick={() => navigate('/template6/shop')} className="group px-8 py-4 bg-stone-900 text-white text-sm font-medium tracking-wider uppercase hover:bg-stone-800 transition-all duration-300 flex items-center gap-3">
                                        Explore Collection <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                    <button className="px-8 py-4 border-2 border-stone-300 text-stone-700 text-sm font-medium tracking-wider uppercase hover:border-stone-900 hover:text-stone-900 transition-all duration-300">
                                        View Lookbook
                                    </button>
                                </div>
                                <div className="flex gap-12 mt-12 pt-8 border-t border-stone-200">
                                    <div><p className="text-3xl font-light text-stone-900">500+</p><p className="text-sm text-stone-500 mt-1">Artisan Partners</p></div>
                                    <div><p className="text-3xl font-light text-stone-900">50k+</p><p className="text-sm text-stone-500 mt-1">Happy Customers</p></div>
                                    <div><p className="text-3xl font-light text-stone-900">25+</p><p className="text-sm text-stone-500 mt-1">States Covered</p></div>
                                </div>
                            </div>
                            <div className="relative">
                                <div className="relative aspect-[3/4] max-w-none">
                                    <div className="relative z-10 overflow-hidden rounded-t-full">
                                        <img src="https://placehold.co/600x800/FEF7ED/1C1917?text=Hero+Model" alt="Featured Collection" className="w-full h-full object-cover" />
                                    </div>
                                    <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-amber-300 rounded-t-full -z-10" />
                                    <div className="absolute -left-6 top-1/3 bg-white shadow-xl px-6 py-4 rounded-lg">
                                        <p className="text-xs text-stone-500 uppercase tracking-wider">Handcrafted</p>
                                        <p className="text-lg font-semibold text-stone-900">In India</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Brand Introduction */}
                <section className="py-20 lg:py-28 bg-white">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <span className="inline-block w-12 h-[2px] bg-amber-600 mb-8" />
                        <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mb-6 leading-relaxed">
                            "We believe in fashion that honors the hands that create it and the culture it represents"
                        </h2>
                        <p className="text-stone-500 text-lg max-w-2xl mx-auto leading-relaxed">
                            Each piece in our collection is a celebration of Indian artistry,
                            crafted by skilled artisans using techniques passed down through generations.
                        </p>
                        <div className="mt-10 flex items-center justify-center gap-6">
                            <img src="https://placehold.co/60x60/FEF7ED/1C1917?text=GI" alt="GI Tag" className="h-12" />
                            <img src="https://placehold.co/60x60/FEF7ED/1C1917?text=HI" alt="Handloom India" className="h-12" />
                            <img src="https://placehold.co/60x60/FEF7ED/1C1917?text=MI" alt="Made in India" className="h-12" />
                        </div>
                    </div>
                </section>

                {/* Categories Grid */}
                <section className="py-16 lg:py-24 bg-amber-50/50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12 lg:mb-16">
                            <span className="text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase">Shop By Category</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mt-3">Curated Collections</h2>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                            {desktopCategories.map((category, index) => (
                                <div key={index} onClick={() => navigate('/template6/shop')} className="group cursor-pointer relative overflow-hidden bg-white rounded-lg shadow-sm hover:shadow-xl transition-all duration-500">
                                    <div className="aspect-[3/4] overflow-hidden">
                                        <img src={category.image} alt={category.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                                        <h3 className="text-white text-lg lg:text-xl font-medium mb-1">{category.name}</h3>
                                        <p className="text-white/70 text-sm">{category.count}</p>
                                    </div>
                                    <div className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                                        <ArrowRight className="w-4 h-4 text-stone-900" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Editorial Spread */}
                <section className="py-16 lg:py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12 lg:mb-16">
                            <span className="text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase">From The Magazine</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mt-3">Editorial Stories</h2>
                        </div>
                        <div className="relative overflow-hidden rounded-2xl bg-amber-50 mb-8">
                            <div className="grid lg:grid-cols-2">
                                <div className="aspect-video lg:aspect-auto lg:h-[500px] overflow-hidden">
                                    <img src={editorials[activeEditorial].image} alt={editorials[activeEditorial].title} className="w-full h-full object-cover" />
                                </div>
                                <div className="p-8 lg:p-12 flex flex-col justify-center">
                                    <span className="text-amber-700 text-sm font-medium tracking-wider uppercase mb-4">{editorials[activeEditorial].subtitle}</span>
                                    <h3 className="text-3xl lg:text-4xl font-light text-stone-900 mb-6">{editorials[activeEditorial].title}</h3>
                                    <p className="text-stone-600 text-lg leading-relaxed mb-8">{editorials[activeEditorial].description}</p>
                                    <button onClick={() => navigate('/template6/shop')} className="self-start group flex items-center gap-3 text-stone-900 font-medium hover:text-amber-700 transition-colors">
                                        {editorials[activeEditorial].cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-center gap-4">
                            {editorials.map((_, index) => (
                                <button key={index} onClick={() => setActiveEditorial(index)} className={`w-20 h-1 rounded-full transition-all duration-300 ${activeEditorial === index ? 'bg-amber-600' : 'bg-stone-200 hover:bg-stone-300'}`} />
                            ))}
                        </div>
                    </div>
                </section>

                {/* Featured Products */}
                <section className="py-16 lg:py-24 bg-gradient-to-b from-amber-50/50 to-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex lg:items-end lg:justify-between mb-12 lg:mb-16">
                            <div>
                                <span className="text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase">New Arrivals</span>
                                <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mt-3">Editor's Picks</h2>
                            </div>
                            <button onClick={() => navigate('/template6/shop')} className="group flex items-center gap-2 text-stone-700 font-medium hover:text-amber-700 transition-colors">
                                View All Products <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                            {desktopProducts.map((product) => (
                                <div key={product.id} onClick={() => navigate('/template6/shop')} className="group cursor-pointer">
                                    <div className="relative aspect-[3/4] overflow-hidden bg-amber-50 rounded-lg mb-4">
                                        <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        <span className="absolute top-3 left-3 px-3 py-1 bg-white/95 text-stone-800 text-xs font-medium rounded-full">{product.badge}</span>
                                        <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                                            <button className="w-full py-3 bg-stone-900 text-white text-sm font-medium hover:bg-stone-800 transition-colors rounded">Quick View</button>
                                        </div>
                                        <button className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white">
                                            <Heart className="w-4 h-4 text-stone-700" />
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        <h3 className="text-stone-900 font-medium group-hover:text-amber-700 transition-colors line-clamp-1">{product.name}</h3>
                                        <div className="flex items-center gap-2">
                                            <span className="text-stone-900 font-semibold">{product.price}</span>
                                            <span className="text-stone-400 text-sm line-through">{product.originalPrice}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Craftsmanship */}
                <section className="py-16 lg:py-24 bg-stone-900 text-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                            <div>
                                <span className="text-amber-400 text-sm font-semibold tracking-[0.2em] uppercase">Our Promise</span>
                                <h2 className="text-3xl lg:text-4xl font-light text-white mt-3 mb-6">Celebrating Indian Craftsmanship</h2>
                                <p className="text-stone-300 text-lg leading-relaxed mb-8">
                                    Every thread tells a story. From the silk weavers of Varanasi to the block printers of Jaipur,
                                    we work directly with artisan communities across India, ensuring fair wages and preserving
                                    traditional techniques for future generations.
                                </p>
                                <div className="grid grid-cols-2 gap-6 mb-8">
                                    {[
                                        { icon: Shield, title: 'Authentic Handloom', desc: 'GI tagged products' },
                                        { icon: Truck, title: 'Free Shipping', desc: 'Pan-India delivery' },
                                        { icon: RefreshCw, title: 'Easy Returns', desc: '15-day return policy' },
                                        { icon: Clock, title: 'COD Available', desc: 'Cash on delivery' }
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-start gap-3">
                                            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                                                <item.icon className="w-5 h-5 text-amber-400" />
                                            </div>
                                            <div>
                                                <h4 className="text-white font-medium mb-1">{item.title}</h4>
                                                <p className="text-stone-400 text-sm">{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={() => navigate('/template6/shop')} className="group px-8 py-4 bg-amber-500 text-stone-900 text-sm font-semibold tracking-wider uppercase hover:bg-amber-400 transition-all duration-300 flex items-center gap-3">
                                    Meet Our Artisans <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                            <div className="relative">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-4">
                                        <div className="aspect-[4/5] overflow-hidden rounded-lg">
                                            <img src="https://placehold.co/400x500/44403C/FFFFFF?text=Artisan+1" alt="Artisan at work" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="aspect-square overflow-hidden rounded-lg">
                                            <img src="https://placehold.co/300x300/57534E/FFFFFF?text=Craft" alt="Craftsmanship" className="w-full h-full object-cover" />
                                        </div>
                                    </div>
                                    <div className="space-y-4 pt-8">
                                        <div className="aspect-square overflow-hidden rounded-lg">
                                            <img src="https://placehold.co/300x300/78716C/FFFFFF?text=Weaving" alt="Weaving" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="aspect-[4/5] overflow-hidden rounded-lg">
                                            <img src="https://placehold.co/400x500/44403C/FFFFFF?text=Artisan+2" alt="Artisan detail" className="w-full h-full object-cover" />
                                        </div>
                                    </div>
                                </div>
                                <button className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-transform">
                                    <Play className="w-8 h-8 text-stone-900 ml-1" />
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Testimonials */}
                <section className="py-16 lg:py-24 bg-amber-50/50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12 lg:mb-16">
                            <span className="text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase">Customer Stories</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mt-3">What They're Saying</h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                            {desktopTestimonials.map((testimonial, index) => (
                                <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300">
                                    <Quote className="w-10 h-10 text-amber-200 mb-4" />
                                    <div className="flex gap-1 mb-4">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                                        ))}
                                    </div>
                                    <p className="text-stone-600 leading-relaxed mb-6">"{testimonial.text}"</p>
                                    <div className="border-t border-stone-100 pt-4">
                                        <p className="font-medium text-stone-900">{testimonial.name}</p>
                                        <p className="text-stone-500 text-sm">{testimonial.location}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Newsletter */}
                <section className="py-16 lg:py-24 bg-gradient-to-br from-amber-100 via-orange-50 to-amber-50">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <span className="text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase">Stay Connected</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mt-3 mb-4">Join Our Community</h2>
                        <p className="text-stone-600 text-lg mb-8">
                            Subscribe for exclusive previews, styling tips, and special offers. Plus, get 15% off your first order!
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                            <input type="email" placeholder="Enter your email" className="flex-1 px-6 py-4 bg-white border border-stone-200 rounded-lg text-stone-900 placeholder-stone-400 focus:outline-none focus:border-amber-500 transition-colors" />
                            <button className="px-8 py-4 bg-stone-900 text-white text-sm font-semibold tracking-wider uppercase hover:bg-stone-800 transition-colors rounded-lg whitespace-nowrap">Subscribe</button>
                        </div>
                        <p className="text-stone-500 text-sm mt-4">No spam, unsubscribe anytime. Privacy policy applies.</p>
                    </div>
                </section>

                {/* Instagram */}
                <section className="py-16 lg:py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <div className="flex items-center justify-center gap-3 mb-3">
                                <Instagram className="w-6 h-6 text-stone-900" />
                                <span className="text-stone-900 font-medium">@symmetryfashion</span>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-light text-stone-900">Follow Our Journey</h2>
                        </div>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 lg:gap-4">
                            {desktopInstagramPosts.map((post, index) => (
                                <div key={index} className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer">
                                    <img src={post.image} alt={`Instagram post ${index + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/40 transition-colors duration-300 flex items-center justify-center">
                                        <div className="flex items-center gap-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Heart className="w-5 h-5" />
                                            <span className="text-sm font-medium">{post.likes}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Services Bar */}
                <section className="py-12 bg-stone-100">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                            {[
                                { icon: Truck, title: 'Free Shipping', desc: 'On orders above \u20b9999' },
                                { icon: RefreshCw, title: 'Easy Returns', desc: '15-day return policy' },
                                { icon: Shield, title: 'Secure Payment', desc: '100% secure checkout' },
                                { icon: Clock, title: 'COD Available', desc: 'Pay on delivery' }
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                        <item.icon className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-stone-900">{item.title}</p>
                                        <p className="text-stone-500 text-sm">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

            </div>

        </div>
    );
};

export default Template6;
